import { Account } from "@services/account/types";
import { Device } from "@services/devices/types";
import { Room } from "@services/rooms/types";
import { ListPage, Priority, Indexed } from "./../common/types";
import { CustomDateFilter } from "@common/components/DatePopover";

export type IndexedTicket = Ticket & Indexed;
export type IndexedTicketsMeta = Pick<
  ListPage<Ticket>,
  "size" | "total" | "index"
>;

export interface TicketsCounters {
  ticketsTotal?: number;
  ticketsCritical?: number;
  ticketsMajor?: number;
  ticketsMinor?: number;
  ticketsCleared?: number;
  ticketsInformational?: number;
}

export interface Ticket {
  triggerName?: string;
  subject?: string;
  meetings?: TicketMeeting[];
  priority?: Priority;
  cleared: boolean;
  device?: Device;
  room?: Room;
  details?: TicketDetails;
  alertPriority?: Priority;
  id: number;
  lastUpdatedDate?: number;
  issue?: TicketIssue;
  openDate?: number;
  eventCount?: number;
  status?: TicketStatus;
  account?: Account;
  talCaseNo?: string;
  talState?: string;
  syncDate?: number;
  syncStatus?: string;
  timeFilter?: CustomDateFilter;
  assignedTo?: string;
  thirdPartyTicketId?: string;
}

export enum TicketStatus {
  Open = "Open",
  Closed = "Closed",
  ClosePending = "ClosePending",
}

export interface TicketIssue {
  type?: string;
  ticket?: Ticket;
  subTypes?: string[];
  id: number;
  resolution?: string;
  issue?: string;
  source?: string;
  comment?: string;
  ticketIssueTime?: number;
}

export interface TicketMeeting {
  confirmationNumber?: number;
  startTime?: number;
  id?: number;
  meetingInstanceId?: number;
  title?: string;
  lastUpdate?: number;
}

export interface TicketDetails {
  deviceConfigurationItemID?: string;
  description?: string;
  id?: number;
  symphonyUrl?: string;
  closedBy?: string;
  externalTicket?: ExternalTicket;
  roomConfigurationItemID?: string;
  events?: TicketEvent[];
  closedDate?: number;
  notes?: TicketNote[];
  requester?: string;
  ticket?: Ticket;
  attachments?: TicketAttachment[];
}

/**
 * As the event subject is displayed nowhere, we can use it to have a distinct one for the ai_troubleshooting,
 * we're using it to correctly display the AI's response as markdown.
 */
export const AI_TROUBLESHOOT_EVENT_SUBJECT =
  "troubleshoot_ai_added_to_timeline";
export interface TicketEvent {
  subject?: string;
  createDate: number;
  cleared?: boolean;
  type?: string;
  description?: string;
  priority?: Priority;
  id?: number;
}

export interface TicketNote {
  externalNoteId?: string;
  note: string;
  createDate?: number;
  creator?: string;
  id?: number;
}

export interface TicketAttachment {
  id: number;
  data?: string;
  createdDate?: number;
  creator?: string;
  symphonyUrl?: string;
  size?: number;
  ticketDetails?: TicketDetails;
  name?: string;
  externalId?: string;
  url?: string;
}

export interface ExternalTicket {
  externalTicketStatus?: string;
  contact?: ExternalTicketContact;
  caseNo?: string;
  parentCaseNo?: string;
  extraProperties?: { [key: string]: string };
  ticketDetails?: TicketDetails;
  datetimeClosed?: number;
  link?: string;
  datetimeAssigned?: number;
  nextStep?: string;
  datetimeOpen?: number;
  assignedTo?: string;
  externalId?: string;
  id: number;
  source?: string;
}

export interface ExternalTicketContact {
  email?: string;
  phone?: string;
  ticket?: ExternalTicket;
  name?: string;
  id: number;
}

export interface TicketsFilters {
  accountId?: string[];
  id?: string;
  priority?: string[];
  subject?: string;
  ["device.id"]?: string[];
  ["room.id"]?: string[];
  ["room.name"]?: string[];
  ["room.location.id"]?: string;
  ["room.location.name"]?: string;
  ["room.location.country"]?: string;
  ["meetings.confirmationNumber"]?: string[];
  ["meetings.meetingInstanceId"]?: string;
  ["device.name"]?: string;
  ["details.requester"]?: string[];
  status?: string[];
  assignedTo?: string;
  ["lastUpdatedDate.from"]?: string;
  ["lastUpdatedDate.to"]?: string;
  cleared?: boolean;
  deviceOnly?: boolean;
  ["device.status.inCall"]?: boolean;
  ["device.status.reachable"]?: boolean;
  ["device.status.stale"]?: boolean;
  ["device.status.apiError"]?: boolean;
  ["device.status.loginError"]?: boolean;
  ["device.status.pauseMonitoringStatus.paused"]?: boolean;
  ["device.type"]?: string;
  ["device.category"]?: string;
  ["device.manufacturer"]?: string;
  ["device.model"]?: string;
  "start.after"?: number;
  "end.before"?: number;
  maxResults?: number;
  ["issue.issue"]?: string;
  syncStatus?: string;
  talState?: string;
  talCaseNo?: string;
}

export type TicketsCount = number | undefined;

export interface AdapterStatus {
  Status: string;
  "Error Details": string;
}

export interface TicketAdapter {
  id: number;
  accountId?: string;
  type?: string;
  ticketSyncType: TicketSyncType;
  properties?: Record<string, string>;
  availableTypes: TicketAdapterType[];
  adapterStatus?: AdapterStatus;
  active: boolean;
}

export enum TicketSyncType {
  None = "None",
  "AVI-SPL" = "AVI-SPL",
  Customer = "Customer",
}

export interface TicketAdapterType {
  id: number;
  type: string;
  properties: TicketAdapterProperty[];
}

export interface TicketAdapterProperty {
  name: string;
  type: string;
  order: number;
  required: boolean;
  secure: boolean;
}

export interface TicketPayload {
  name: string;
  variable: string;
  format: string;
  example: string;
  description: string;
}
